import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { useInRouterContext, useNavigate } from "react-router-dom";

import toast from "react-hot-toast";

import { createMentionRegex, parseMentionAmountHours } from "../../utils/text";
import {
  DEEP_LINK_ICONS,
  parseInternalLink,
  type InternalLink,
} from "../../utils/deepLink";
import {
  downloadFile,
  DOWNLOAD_FAILED_MESSAGE,
} from "../../utils/downloadFile";
import AttachmentFileCard from "../file-attachment/AttachmentFileCard";
import MarkdownAttachmentPreview from "./MarkdownAttachmentPreview";
import { ShotCommentLinkHover } from "./ShotCommentHoverCard";
import MediaPreviewModal from "../media-preview/components/MediaPreviewModal";
import { EmbeddedMediaContext } from "./embeddedMedia";
import {
  attachmentFileName,
  isAttachmentLink,
  isEmbeddedUploadUrl,
} from "./markdownAttachment";

/**
 * Markdown 表示の共通コンポーネント。
 *
 * チャット（MessageItem）・タスクボードのセル・ノート・プロフィールなど、
 * 本文を Markdown として描画する全箇所で共有する。GFM（テーブル・タスクリスト・
 * 取り消し線）に対応し、rehype-sanitize で XSS を抑止する。
 *
 * 見た目は `variant` で切り替える（Props 語彙規約 B-4）:
 * - "chat"（既定）: チャット吹き出し・タスクボードセル向けの高密度スタイル
 * - "document": ノート・プロフィール向けの読み物スタイル
 *   （Notion / Obsidian 風の見出しスケールとブロック余白）
 *
 * タイポグラフィはグローバル要素セレクタ（h1〜h6・p 等）に依存せず、この
 * コンテナ内で完結させる。フォントスタックは soremo_style.scss の typography
 * 系統（AXIS ラウンド L/R/M/B）と同一で、variant で変わるのはサイズと余白のみ。
 *
 * チェックボックス（`- [ ]` / `- [x]`）は既定では readOnly。`interactiveCheckboxes`
 * を渡すとクリックでトグルでき、`onCheckboxToggle(index, checked)` に「本文中の
 * 何番目のチェックボックスか」と「次の状態」を通知する（呼び出し側で本文の
 * `[ ]` ⇔ `[x]` を更新する）。
 *
 * NOTE: Markdown 文字列は children ではなく `content` prop で受ける（グローバル
 * コンポーネント規約 B-3: children は ReactNode のみ）。
 */

export type MarkdownVariant = "chat" | "document";

// soremo_style.scss の typography 系統（.bodytext-* / .heading-*）と同じフォントスタック
const FONT_L = `"A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif`;
const FONT_R = `"A+mfCv-AXISラウンド 50 R StdN", "M PLUS 1p", sans-serif`;
const FONT_M = `"A+mfCv-AXISラウンド 50 M StdN", "M PLUS 1p", sans-serif`;
const FONT_B = `"A+mfCv-AXISラウンド 50 B StdN", "M PLUS 1p", sans-serif`;

/* ── chat バリアント（既定） ─────────────────────────────────────────
   .bodytext-13 + .u-line-height-150 相当の高密度スタイル（従来の見た目を維持）。
   見出しはグローバル h1〜h6（最大 40px・letter-spacing 2.5px）に任せると
   吹き出し内で暴発するため、本文基準の控えめなスケールに抑える。 */
const chatVariantCss = css`
  font-family: ${FONT_L};
  font-size: 13px;
  line-height: 150%;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    line-height: 150%;
    letter-spacing: 0.02em;
    margin: 0.5em 0 0.25em;
  }
  & h1 {
    font-family: ${FONT_M};
    font-size: 1.25em;
  }
  & h2 {
    font-family: ${FONT_M};
    font-size: 1.15em;
  }
  & h3,
  & h4,
  & h5,
  & h6 {
    font-family: ${FONT_R};
    font-size: 1em;
  }

  & p {
    margin: 0;
  }

  & ul,
  & ol {
    margin: 4px 0;
  }
  & ul li {
    margin: 0 0 0 16px;
    list-style-type: disc;
  }
  & ol li {
    margin: 0 0 0 16px;
    list-style-type: decimal;
  }

  & blockquote {
    margin: 0;
    padding-left: 10px;
    color: var(--soremo-light-gray);
  }

  & pre {
    /* 旧 .c-group + インライン padding の合成と同じ見た目 */
    border: 1px solid var(--soremo-border);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.5);
    padding: 4px;
    margin: 0;
  }

  & th,
  & td {
    padding: 8px 16px;
    text-align: center;
  }
`;

/* ── document バリアント ─────────────────────────────────────────────
   ノート・プロフィール向けの読み物スタイル。本文は .bodytext 相当
   （13px→16px の clamp）、見出しは em ベースの階層スケール、ブロック間に
   Notion / Obsidian 風の余白リズムを持たせる。 */
const documentVariantCss = css`
  font-family: ${FONT_L};
  /* .bodytext 相当（viewport 320px→640px で 13px→16px） */
  font-size: clamp(0.813rem, 0.625rem + 0.94vw, 1rem);
  line-height: 175%;
  overflow-wrap: break-word;

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    line-height: 145%;
    letter-spacing: 0.02em;
  }
  & h1 {
    font-family: ${FONT_M};
    font-size: 1.6em;
    margin: 1.75em 0 0.5em;
  }
  & h2 {
    font-family: ${FONT_M};
    font-size: 1.35em;
    margin: 1.5em 0 0.5em;
  }
  & h3 {
    font-family: ${FONT_R};
    font-size: 1.2em;
    margin: 1.25em 0 0.4em;
  }
  & h4 {
    font-family: ${FONT_R};
    font-size: 1.05em;
    margin: 1.1em 0 0.3em;
  }
  & h5 {
    font-family: ${FONT_R};
    font-size: 1em;
    margin: 1em 0 0.3em;
  }
  & h6 {
    font-family: ${FONT_R};
    font-size: 0.95em;
    margin: 1em 0 0.3em;
    color: var(--soremo-gray);
  }

  & p {
    margin: 0 0 0.8em;
  }

  & ul,
  & ol {
    margin: 0.4em 0 0.8em;
    padding-left: 1.4em;
  }
  & ul li {
    list-style-type: disc;
    margin: 0.25em 0;
  }
  & ul ul li {
    list-style-type: circle;
  }
  & ul ul ul li {
    list-style-type: square;
  }
  & ol li {
    list-style-type: decimal;
    margin: 0.25em 0;
  }
  & li > ul,
  & li > ol {
    margin: 0.25em 0;
  }
  /* loose list（li 内が段落になるケース）でも行間を崩さない */
  & li p {
    margin: 0;
  }

  & blockquote {
    margin: 0.8em 0;
    padding: 0.2em 0 0.2em 1em;
    color: var(--soremo-gray);
  }

  & code {
    background: rgba(167, 168, 169, 0.15);
    padding: 2px 6px;
    font-size: 0.875em;
  }
  & pre {
    background: var(--soremo-background);
    border: 1px solid var(--soremo-border);
    border-radius: 6px;
    padding: 12px 16px;
    margin: 0.8em 0;
    font-size: 0.875em;
  }
  /* コピー UI 付きコードブロック（ラッパー）。pre のブロック余白を引き継ぐ */
  & .md-code-block {
    margin: 0.8em 0;
  }
  & pre code {
    background: none;
    padding: 0;
    font-size: inherit;
  }

  & table {
    margin: 0.8em 0;
  }
  & th,
  & td {
    padding: 6px 12px;
    text-align: left;
  }

  & hr {
    margin: 1.5em 0;
  }

  & ul.contains-task-list {
    margin: 0.4em 0 0.8em;
  }
`;

// マークダウンスタイル用のコンテナ - モバイル対応
export const MarkdownContainer = styled.div<{ $variant?: MarkdownVariant }>`
  /* グローバル要素セレクタ（p・a・li 等の clamp フォント指定）に引きずられず、
     コンテナのベースタイポグラフィを継承させる */
  & p,
  & a,
  & li,
  & blockquote,
  & th,
  & td {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  & p {
    white-space: pre-wrap;
  }

  & a {
    color: var(--soremo-blue);
    text-decoration: none;
    word-break: break-all;
  }

  & strong {
    font-family: ${FONT_B};
    padding: 0 2px;
  }

  & code {
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
    word-break: break-all;
    overflow-wrap: break-word;
  }
  & pre {
    max-width: 100%;
    overflow-x: auto;
  }
  & pre code {
    white-space: pre-wrap;
    display: block;
    width: 100%;
  }

  /* コードブロックのコピー UI。pre は .md-code-block で包んで描画し
     （MarkdownCodeBlock）、ブロック余白はラッパー側（variant CSS）が持つ */
  & .md-code-block {
    position: relative;
  }
  & .md-code-block > pre {
    margin: 0;
  }
  & .md-code-block .code-copy-button {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--soremo-border);
    border-radius: 4px;
    background: #fff;
    color: var(--soremo-light-gray);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease, color 0.15s ease;
  }
  & .md-code-block .code-copy-button .material-symbols-rounded {
    font-size: 16px;
  }
  @media (hover: hover) {
    & .md-code-block:hover .code-copy-button,
    & .md-code-block .code-copy-button:focus-visible {
      opacity: 1;
    }
    & .md-code-block .code-copy-button:hover {
      color: var(--soremo-blue);
    }
  }
  /* ホバーが無いタッチ端末では常時表示する */
  @media (hover: none) {
    & .md-code-block .code-copy-button {
      opacity: 1;
    }
  }

  & blockquote {
    position: relative;
  }
  & blockquote::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: var(--soremo-border);
    border-radius: 2px;
  }
  & blockquote p {
    white-space: pre-wrap;
  }

  & img {
    max-width: 100%;
    max-height: calc(100dvh - 192px);
    border-radius: 4px;
    margin: 4px 0;
    object-fit: contain;
  }
  /* zoomableImages: クリックでモーダル拡大できる画像（リンク内の画像は除く） */
  & img.md-zoomable-image {
    cursor: zoom-in;
  }
  & a img.md-zoomable-image {
    cursor: pointer;
  }

  /* テーブルのスタイル */
  & table {
    border-collapse: collapse;
    width: 100%;
    border-spacing: 0;
  }
  & th {
    font-family: ${FONT_R};
    background: var(--soremo-border);
    border: none;
    border-top: 1px solid var(--soremo-border);
    border-bottom: 1px solid var(--soremo-border);
  }
  & td {
    border: none;
    border-bottom: 1px solid var(--soremo-border);
  }
  & tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.5);
  }

  /* タスクリスト（チェックボックス付きリスト）のスタイル */
  & ul.contains-task-list {
    list-style-type: none;
    padding-left: 0;
    margin: 0;
  }

  & li.task-list-item {
    display: flex;
    align-items: center;
    margin: 4px 0;
    font-size: inherit;
  }

  /* 改行のみの行を挟むと GFM の loose list になり、li の中身（チェックボックス
     と本文）が <p> で包まれて上の flex 整列（li 直下前提）が崩れる。<p> を
     レイアウト上透明にして、通常の（tight な）リストと同じ見た目に揃える。 */
  & li.task-list-item > p {
    display: contents;
  }

  & .task-list-item-checkbox {
    margin-right: 4px;
    appearance: none;
    -webkit-appearance: none;
    width: 24px;
    height: 24px;
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* チェックボックスの色。同じ行に @表示名 があり、呼び出し側が
     checkboxColorOf で色を割り当てた場合は、その色（--task-checkbox-color、
     rehype プラグインが input の style に埋め込む）を優先する。 */
  & .task-list-item-checkbox::before {
    content: "check_box_outline_blank";
    font-family: 'Material Symbols Rounded';
    font-size: 24px;
    color: var(--task-checkbox-color, var(--soremo-light-gray));
  }

  & .task-list-item-checkbox:checked::before {
    content: "select_check_box";
    color: var(--task-checkbox-color, var(--soremo-light-gray));
  }

  /* タイムスタンプリンク [MM:SS] — タグ風デザイン */
  & a[href^="#ts-"] {
    display: inline-block;
    background: var(--soremo-light-gray);
    color: #fff;
    font-size: 11px;
    line-height: 150%;
    padding: 0px 4px;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    vertical-align: middle;
    transition: background 0.15s;

    &:hover {
      background: var(--soremo-blue);
    }
  }

  /* メンションの作業時間 @山田[3h] — タイムスタンプリンクと揃えたタグ風デザイン。
     集計対象として解釈された数量だけがバッジになるので、素のテキストのままなら
     記法ミスだと書いた本人が気づける。 */
  & [data-mention] > .mention-amount {
    display: inline-block;
    margin-left: 2px;
    padding: 0px 4px;
    border-radius: 4px;
    background: var(--soremo-border);
    color: var(--soremo-light-gray);
    font-size: 11px;
    line-height: 150%;
    vertical-align: middle;
  }

  /* コンテナ先頭・末尾のブロックは外側の余白と二重にしない */
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }

  /* variant 別スタイル（後方に置き、共通定義を上書きできるようにする） */
  ${({ $variant }) =>
    $variant === "document" ? documentVariantCss : chatVariantCss}
`;

// サニタイズオプション
export const sanitizeOptions = {
  // 許可するタグを指定
  allowedTags: [
    'a', 'b', 'blockquote', 'br', 'code', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'hr', 'i', 'img', 'input', 'li', 'ol', 'p', 'pre', 's', 'strong', 'table', 'td', 'th',
    'tr', 'ul'
  ],
  // 許可する属性を指定
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt', 'title'],
    input: ['type', 'checked', 'disabled'],
    '*': ['class']
  },
  // 外部リンクには自動的にtarget="_blank"とrel="noopener noreferrer"を追加
  // （同一オリジンの内部リンクは SPA 内遷移させるため付けない）
  transformTags: {
    'a': (tagName: string, attribs: { [key: string]: string }) => {
      if (attribs.href && attribs.href.startsWith('http')) {
        try {
          if (new URL(attribs.href).origin === window.location.origin) {
            return { tagName, attribs };
          }
        } catch {
          // 不正な URL は外部リンク扱いにフォールバック
        }
        return {
          tagName,
          attribs: {
            ...attribs,
            target: '_blank',
            rel: 'noopener noreferrer'
          }
        };
      }
      return { tagName, attribs };
    }
  }
};

// ── @メンション装飾（rehype プラグイン） ─────────────────────────────
//
// `renderMentionSuffix` が渡されたときだけ有効化する。sanitize 後の hast を走査し、
// テキストノード中の `@表示名`（utils/text.ts の createMentionRegex の規則）を
// `<span data-mention="表示名">@表示名</span>` に分割する。表示は components の
// span マッピングで行い、メンション直後に呼び出し側の suffix（バッジ等）を挿す。

/** hast ノードの最小構造型（依存追加を避けるためローカル定義） */
interface HastText {
  type: "text";
  value: string;
}
interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}
type HastNode = HastText | HastElement | { type: string; children?: HastNode[] };

/** メンションを含まない文脈（リンク・コード）は装飾しない */
const MENTION_SKIP_TAGS = new Set(["a", "code", "pre"]);

/** テキストを text / span(data-mention) ノード列に分割する。メンションが無ければ null */
const splitMentionNodes = (value: string): HastNode[] | null => {
  const mentionRe = createMentionRegex();
  const out: HastNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = mentionRe.exec(value)) !== null) {
    const prefix = m[1] ?? "";
    const name = m[2];
    const start = m.index + prefix.length;
    // マッチ全体を消費する（`@山田[3h]` の数量表記まで 1 トークンとして扱う）
    const end = m.index + m[0].length;
    const nameEnd = start + 1 + name.length;
    // 数量表記の原文（`[3h]` 等）。書いたとおりの表記を保って見せる
    const amountText = value.slice(nameEnd, end);
    const hours = parseMentionAmountHours(m[3], m[4]);
    if (start > last) out.push({ type: "text", value: value.slice(last, start) });

    const children: HastNode[] = [{ type: "text", value: `@${name}` }];
    if (amountText) {
      // 数量はバッジとして描き分ける。集計対象として解釈されたことが
      // 書いた本人にその場で分かるようにするため（記法ミスの早期発見）。
      children.push({
        type: "element",
        tagName: "span",
        properties: { className: ["mention-amount"] },
        children: [{ type: "text", value: amountText }],
      });
    }
    out.push({
      type: "element",
      tagName: "span",
      properties: {
        dataMention: name,
        ...(hours != null ? { dataMentionHours: String(hours) } : {}),
      },
      children,
    });
    last = end;
  }
  if (out.length === 0) return null;
  if (last < value.length) {
    out.push({ type: "text", value: value.slice(last) });
  }
  return out;
};

const walkAndSplitMentions = (node: HastNode): void => {
  if (!("children" in node) || !node.children) return;
  if (
    node.type === "element" &&
    MENTION_SKIP_TAGS.has((node as HastElement).tagName)
  ) {
    return;
  }
  for (let i = node.children.length - 1; i >= 0; i -= 1) {
    const child = node.children[i];
    if (child.type === "text") {
      const replaced = splitMentionNodes((child as HastText).value);
      if (replaced) node.children.splice(i, 1, ...replaced);
    } else {
      walkAndSplitMentions(child);
    }
  }
};

/** sanitize の後段に差し込む rehype プラグイン */
const rehypeMentionSpans = () => (tree: unknown) => {
  walkAndSplitMentions(tree as HastNode);
};

// ── チェックボックスのユーザー配色（rehype プラグイン） ────────────────
//
// `checkboxColorOf` が渡されたときだけ有効化する。タスクリスト行
// （li.task-list-item）のテキストから `@表示名` を拾い、割り当てられた色を
// その行のチェックボックス（input）へ CSS 変数（--task-checkbox-color）として
// 埋め込む。実際の色は MarkdownContainer 側の
// `.task-list-item-checkbox::before` が変数経由で参照する。

/** 入れ子リストを除いた、そのタスクリスト行自身のテキストを集める */
const ownTextOfListItem = (node: HastNode): string => {
  if (node.type === "text") return (node as HastText).value;
  if (!("children" in node) || !node.children) return "";
  if (
    node.type === "element" &&
    ((node as HastElement).tagName === "ul" ||
      (node as HastElement).tagName === "ol")
  ) {
    // 入れ子のリストは別の li として個別に処理される
    return "";
  }
  return node.children.map((c) => ownTextOfListItem(c)).join("");
};

/** タスクリスト行直下のチェックボックス input を探す（入れ子リストは対象外） */
const findCheckboxInput = (node: HastNode): HastElement | null => {
  if (node.type === "element") {
    const el = node as HastElement;
    if (el.tagName === "ul" || el.tagName === "ol") return null;
    if (el.tagName === "input") return el;
  }
  if (!("children" in node) || !node.children) return null;
  for (const child of node.children) {
    const found = findCheckboxInput(child);
    if (found) return found;
  }
  return null;
};

const walkTaskListCheckboxColors = (
  node: HastNode,
  // eslint-disable-next-line no-unused-vars
  colorOf: (name: string) => string | undefined | null,
): void => {
  if (!("children" in node) || !node.children) return;
  for (const child of node.children) {
    if (child.type === "element") {
      const el = child as HastElement;
      const classes = el.properties?.className;
      const isTaskItem =
        el.tagName === "li" &&
        Array.isArray(classes) &&
        classes.includes("task-list-item");
      if (isTaskItem) {
        // 行内の最初に色を持つメンションを採用する
        const text = ownTextOfListItem(el);
        const mentionRe = createMentionRegex();
        let color: string | undefined | null;
        let m: RegExpExecArray | null;
        while ((m = mentionRe.exec(text)) !== null) {
          color = colorOf(m[2]?.trim() ?? "");
          if (color) break;
        }
        if (color) {
          const input = findCheckboxInput(el);
          if (input) {
            input.properties = {
              ...(input.properties ?? {}),
              style: `--task-checkbox-color: ${color};`,
            };
          }
        }
      }
    }
    walkTaskListCheckboxColors(child, colorOf);
  }
};

// ── <br> 直後の改行文字の除去（rehype プラグイン） ─────────────────────
//
// mdast-util-to-hast は Markdown の hard break（行末の `\` や半角スペース 2 つ、
// Tiptap の Shift+Enter もこれで保存される）を `<br>` + テキスト `"\n"` の 2 ノードに
// 変換する。この `"\n"` は HTML ソースを整形するためのもので通常は空白として畳まれるが、
// 本文の `p` は `white-space: pre-wrap` のため 2 本目の改行として描画されてしまい、
// 「1 行の改行」が段落の間隔より広く見える。編集中（ProseMirror）と表示を揃えるため、
// `<br>` の直後にある改行 1 文字だけを取り除く。

/** 原文の空白をそのまま見せる要素（この配下は触らない） */
const PREFORMATTED_TAGS = new Set(["pre", "textarea"]);

const stripBreakNewlines = (node: HastNode): void => {
  if (!("children" in node) || !node.children) return;
  if (
    node.type === "element" &&
    PREFORMATTED_TAGS.has((node as HastElement).tagName)
  ) {
    return;
  }
  for (let i = 0; i < node.children.length; i += 1) {
    const child = node.children[i];
    const prev = i > 0 ? node.children[i - 1] : undefined;
    if (
      child.type === "text" &&
      (child as HastText).value.startsWith("\n") &&
      prev?.type === "element" &&
      (prev as HastElement).tagName === "br"
    ) {
      const text = child as HastText;
      text.value = text.value.slice(1);
      if (text.value === "") {
        node.children.splice(i, 1);
        i -= 1;
        continue;
      }
    }
    stripBreakNewlines(child);
  }
};

const rehypeBreakNewlines = () => (tree: unknown) => {
  stripBreakNewlines(tree as HastNode);
};

// ── リンク描画 ────────────────────────────────────────────────────────
//
// 外部リンク: 従来どおり新しいタブで開く（target="_blank"）。
// 同一オリジンの内部リンク: SPA 内遷移（navigate）。ディープリンク
// （イメージボード / ノート / ライブラリ）は種別アイコン付きのチップとして
// 描画し、トークルーム / ショットコメント / DM に貼った「場所を示すリンク」を
// ひと目で区別できるようにする。
// ページ内 hash（MessageItem の `#ts-`、NoteView の `#note-`）は
// parseInternalLink が対象外にするため従来の委任クリック処理を壊さない。

/**
 * 内部ディープリンクのチップ。HashTagChip / ContentTag と同じ pill の視覚言語
 * （--soremo-background 地 + --soremo-border 枠）+ 種別アイコン。テキストは
 * bodytext-11 相当・黒で本文になじませ、ホバーで soremo-blue に変わることで
 * リンクであることを示す。
 */
const InternalLinkChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px 2px 6px;
  border: 1px solid var(--soremo-border);
  border-radius: 999px;
  background: var(--soremo-background);
  vertical-align: middle;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;

  /* MarkdownContainer の & a（inherit / soremo-blue）より強くするため && */
  && {
    font-family: ${FONT_L};
    font-size: 11px;
    line-height: 150%;
    color: #000;
    text-decoration: none;
  }

  & > .material-symbols-rounded {
    font-size: 14px;
    color: var(--soremo-light-gray);
    flex-shrink: 0;
  }

  & > .internal-link-label {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  @media (hover: hover) {
    &&:hover {
      border-color: var(--soremo-light-gray);
      background: #fff;
      color: var(--soremo-blue);
    }
  }
`;

/** SPA 内遷移するアンカー（Router 配下でのみ使われる）。 */
const SpaAnchor: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { internal: InternalLink }
> = ({ internal, children, ...props }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // 修飾キー付き・中クリックは「新しいタブで開く」等のブラウザ既定に任せる
    if (
      e.defaultPrevented ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    e.preventDefault();
    navigate(internal.to);
  };

  if (internal.kind) {
    const chip = (
      <InternalLinkChip {...props} onClick={handleClick}>
        <span className="material-symbols-rounded">
          {DEEP_LINK_ICONS[internal.kind]}
        </span>
        <span className="internal-link-label">{children}</span>
      </InternalLinkChip>
    );
    // ショットコメントはホバーだけでコメント内容とピン先メディアを覗き見できる
    if (internal.kind === "shot-comment") {
      return <ShotCommentLinkHover to={internal.to}>{chip}</ShotCommentLinkHover>;
    }
    return chip;
  }
  return (
    <a {...props} onClick={handleClick}>
      {children}
    </a>
  );
};

/** リンクラベル等の ReactNode からプレーンテキストを取り出す。 */
const textOfNode = (node: React.ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOfNode).join("");
  if (React.isValidElement(node)) {
    return textOfNode((node.props as { children?: React.ReactNode }).children);
  }
  return "";
};

/* Markdown 本文の流れに置くための余白（メッセージ側のレイアウトには影響しない） */
const AttachmentCardAnchor = styled(AttachmentFileCard)`
  margin: 4px 0;
`;

/**
 * 共有ノートの添付（`[📎 名前](S3 URL)`）を、メッセージ添付と同じ
 * ファイルカード（AttachmentFileCard）で表示する。カード全体が <a>
 * （href モード）なので <p> 内でも valid な HTML になる。
 * クリックで元のファイル名を付けて強制ダウンロードする（公開 S3 URL は
 * Content-Disposition を持たないため fetch → Blob 経由で保存名を指定する）。
 * 修飾キー付き・中クリックは「新しいタブで開く」等のブラウザ既定に任せる。
 */
const MarkdownAttachmentLink: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => {
  const fileName = attachmentFileName(label, href);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      e.defaultPrevented ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    e.preventDefault();
    void downloadFile(href, fileName).then((succeeded) => {
      if (!succeeded) toast.error(DOWNLOAD_FAILED_MESSAGE);
    });
  };

  return (
    <AttachmentCardAnchor
      href={href}
      fileName={fileName}
      onClick={handleClick}
    />
  );
};

/**
 * リンク: 内部リンク（同一オリジン）なら SPA 内遷移（ディープリンクは
 * チップ表示）、ノート添付（S3 notes/ 配下の非メディア）ならファイルカード
 * 表示、外部リンク・Router 外・パース不能なら従来どおり新しいタブで開く。
 * ディープリンク（/notes/{noteId} 等）が添付カードへ化けないよう、内部
 * リンクの判定を添付判定より先に行う。
 */
const MarkdownAnchor: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    node?: unknown;
    attachmentDisplay?: AttachmentDisplay;
  }
> = ({ children, node, href, attachmentDisplay = "default", ...props }) => {
  void node;
  // useNavigate は Router 外で throw するため、先にコンテキストの有無を見る
  const inRouter = useInRouterContext();
  const internal = inRouter ? parseInternalLink(href) : null;
  if (internal) {
    return (
      <SpaAnchor href={href} internal={internal} {...props}>
        {children}
      </SpaAnchor>
    );
  }
  if (attachmentDisplay !== "default" && isEmbeddedUploadUrl(href)) {
    return (
      <MarkdownAttachmentPreview
        href={href}
        fileName={attachmentFileName(textOfNode(children), href)}
        variant={attachmentDisplay === "preview-card" ? "card" : "chip"}
      />
    );
  }
  if (isAttachmentLink(href)) {
    return <MarkdownAttachmentLink href={href} label={textOfNode(children)} />;
  }
  return (
    <a target="_blank" rel="noopener noreferrer" href={href} {...props}>
      {children}
    </a>
  );
};

/**
 * コードブロック。ホバー（タッチ端末では常時）で右上に「クリップボードに
 * コピー」ボタンを表示する。コピー成功はアイコンの check 変化だけで示す
 * （トーストは出さない）。失敗時のみトーストで知らせる。
 * 見た目は MarkdownContainer の `.md-code-block` / `.code-copy-button` に定義。
 */
const MarkdownCodeBlock: React.FC<
  React.HTMLAttributes<HTMLPreElement> & { node?: unknown }
> = ({ node, children, ...props }) => {
  void node;
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(resetTimerRef.current), []);

  const handleCopy = async () => {
    const text = preRef.current?.textContent ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  return (
    <div className="md-code-block">
      <pre ref={preRef} {...props}>
        {children}
      </pre>
      <button
        type="button"
        className="code-copy-button"
        title="クリップボードにコピー"
        aria-label="コードをクリップボードにコピー"
        onClick={() => void handleCopy()}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          {copied ? "check" : "content_copy"}
        </span>
      </button>
    </div>
  );
};

/**
 * 画像。alt が無ければ補完する。EmbeddedMediaContext の Provider 配下
 * （タスクボード等）では、変換済みの最適化画像があればそちらを遅延読み込みで
 * 表示し、読み込めなければ元画像へ戻す。
 */
const MarkdownImage: React.FC<
  React.ImgHTMLAttributes<HTMLImageElement> & { node?: unknown }
> = ({ alt, node, src, ...props }) => {
  void node;
  const resolve = useContext(EmbeddedMediaContext);
  const [optimizedFailed, setOptimizedFailed] = useState(false);
  const optimized =
    src && resolve && !optimizedFailed ? resolve(src)?.imageUrl : undefined;
  return (
    <img
      alt={alt || "画像"}
      src={optimized || src}
      {...(resolve ? { loading: "lazy" as const, decoding: "async" as const } : {})}
      {...(optimized ? { onError: () => setOptimizedFailed(true) } : {})}
      {...props}
    />
  );
};

/** 画像 URL の末尾からファイル名を取り出す（モーダルの見出し・保存名用） */
const imageFileNameOf = (src: string): string => {
  const last = src.split(/[?#]/)[0].split("/").pop() ?? "";
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

/**
 * モーダル内のイベントは React のポータル越しに本文側へ伝わるため、本文の
 * クリック委任（wikilink 遷移・タイムスタンプ等）を誤発火させないよう止める。
 */
const stopModalPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * zoomableImages 指定時の画像。クリックで MediaPreviewModal を開き、本文幅に
 * 縮められた画像を大きく見せる（開いた時点でビューポートに応じた最大幅・
 * 等倍が上限。元画像がさらに広いときはモーダル内クリックで等倍と切り替え）。
 * リンクで囲まれた画像はリンクを優先し、修飾キー付き・中クリックもブラウザ
 * 既定に任せる。モーダルには元画像（src）を渡す（本文側の最適化画像は縮小版
 * のため）。
 */
const ZoomableMarkdownImage: React.FC<
  React.ImgHTMLAttributes<HTMLImageElement> & { node?: unknown }
> = ({ className, onClick, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { src, alt } = props;

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    onClick?.(e);
    if (
      !src ||
      e.defaultPrevented ||
      e.currentTarget.closest("a") ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      e.button !== 0
    ) {
      return;
    }
    setIsOpen(true);
  };

  const fileName = src ? imageFileNameOf(src) : "";

  return (
    <>
      <MarkdownImage
        {...props}
        className={[className, "md-zoomable-image"].filter(Boolean).join(" ")}
        onClick={handleClick}
      />
      {isOpen && src && (
        <span
          onClick={stopModalPropagation}
          onMouseOver={stopModalPropagation}
          onMouseOut={stopModalPropagation}
          onKeyDown={stopModalPropagation}
        >
          <MediaPreviewModal
            src={src}
            mediaType="image"
            fileName={alt || fileName}
            fitToImage
            onClose={() => setIsOpen(false)}
            onDownload={() => {
              void downloadFile(src, fileName).then((succeeded) => {
                if (!succeeded) toast.error(DOWNLOAD_FAILED_MESSAGE);
              });
            }}
            downloadUrl={src}
          />
        </span>
      )}
    </>
  );
};

// 見た目は MarkdownContainer（variant CSS）に集約しているため、components では
// 「見た目以外の振る舞い」だけを扱う（リンクの内部/外部分岐・alt 補完・
// チェックボックス・メンション・コードブロックのコピー）。
const baseMarkdownComponents = {
  a: MarkdownAnchor,
  pre: MarkdownCodeBlock,
  img: MarkdownImage,
};

/**
 * attachmentDisplay="preview" のときのリンク。components に渡すコンポーネントを
 * レンダリングごとに作り直すと React には毎回「別の型」に見え、リンク配下が
 * 再マウントされて MarkdownAttachmentPreview のプレビューモーダルが開いた直後に閉じて
 * しまう（タスクボードのセルはホバー拡大の state 更新で頻繁に再描画される）。
 * 型を安定させるためモジュールレベルで固定定義する。
 */
const PreviewMarkdownAnchor: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }
> = (props) => <MarkdownAnchor {...props} attachmentDisplay="preview" />;

/** attachmentDisplay="preview-card" 用（型を安定させる理由は PreviewMarkdownAnchor と同じ） */
const PreviewCardMarkdownAnchor: React.FC<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }
> = (props) => <MarkdownAnchor {...props} attachmentDisplay="preview-card" />;

/**
 * 本文へ埋め込まれたアップロードファイル（ノート / タスクボードの S3 URL）の表示。
 * - "default": PDF 等はファイルカード（ダウンロード）、動画・音声は通常リンク
 * - "preview": 種別を問わず小さなチップにし、クリックでプレビューモーダルを開く
 *   （表のセルなど、本文中でメディアを読み込みたくない場所向け）
 * - "preview-card": メッセージ添付と同一のファイルカードで、クリックで
 *   プレビューモーダルを開く。ダウンロードアイコンは直接ダウンロード
 *   （共有ノート向け。メッセンジャーと見た目を揃える）
 */
export type AttachmentDisplay = "default" | "preview" | "preview-card";

export interface MarkdownContentProps {
  /** 描画する Markdown 文字列（children ではなく専用 prop で受ける） */
  content: string;
  /** 表示バリアント。既定は "chat"（チャット/タスクボード密度） */
  variant?: MarkdownVariant;
  /** 埋め込みアップロードファイルの表示方法。既定 "default" */
  attachmentDisplay?: AttachmentDisplay;
  /**
   * true で本文中の改行（soft break）をそのまま改行として描画する
   * （remark-breaks。GitHub コメントや Notion と同じ挙動）。
   * ノート・カード・プロフィールの閲覧で有効化し、リッチ編集
   * （Tiptap の breaks: true）と表示を一致させる。既定 false。
   */
  breaks?: boolean;
  /**
   * true で本文中の画像をクリックしたときにプレビューモーダル（ビューポートに
   * 応じた最大幅・等倍が上限）で拡大表示する。読みやすさのため本文幅を
   * 絞っているノート閲覧向け。既定 false。
   */
  zoomableImages?: boolean;
  className?: string;
  /** クリックのイベント委任ハンドラ（タイムスタンプリンク等） */
  onClick?: (e: React.MouseEvent) => void;
  /** コンテナへの ref */
  containerRef?: React.Ref<HTMLDivElement>;
  /** ReactMarkdown の前に差し込む要素（再生アイコン等） */
  prefix?: React.ReactNode;
  /** true でチェックボックスをクリック可能にする（既定 false = readOnly） */
  interactiveCheckboxes?: boolean;
  /** チェックボックスがトグルされたときに「本文中の N 番目」と次の状態を通知 */
  onCheckboxToggle?: (index: number, checked: boolean) => void;
  /**
   * `@表示名` の直後に差し込む要素（ステータスバッジ等）を返す。
   * 指定時のみ本文中のメンションを検出して装飾する（既定は従来どおりプレーン表示）。
   * `null` を返すとそのメンションには何も付かない。
   * `hours` は `@山田[3h]` 記法で書かれた作業時間（時間換算）。記載が無ければ null。
   */
  renderMentionSuffix?: (
    // eslint-disable-next-line no-unused-vars
    name: string,
    // eslint-disable-next-line no-unused-vars
    hours: number | null,
  ) => React.ReactNode;
  /**
   * タスクリスト行の `@表示名` → チェックボックスの色。指定時のみ各行を走査し、
   * 同じ行に色を持つメンションがあればチェックボックスをその色で塗る
   * （アクティビティ集計のユーザー配色をタスクボードに反映する用途）。
   * undefined / null を返した名前は既定色（ライトグレー）のまま。
   */
  // eslint-disable-next-line no-unused-vars
  checkboxColorOf?: (name: string) => string | undefined | null;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  variant = "chat",
  attachmentDisplay = "default",
  breaks = false,
  zoomableImages = false,
  className,
  onClick,
  containerRef,
  prefix,
  interactiveCheckboxes = false,
  onCheckboxToggle,
  renderMentionSuffix,
  checkboxColorOf,
}) => {
  // 本文中のチェックボックスを上から順に数えるためのカウンタ。
  // ReactMarkdown はソース順に input をレンダリングするため、レンダリング開始前に
  // 0 へリセットし、各 input でインクリメントすることで安定した index を得る。
  const checkboxCounter = useRef(0);
  checkboxCounter.current = 0;

  const components = useMemo(() => {
    return {
      ...baseMarkdownComponents,
      ...(attachmentDisplay === "preview" ? { a: PreviewMarkdownAnchor } : {}),
      ...(attachmentDisplay === "preview-card"
        ? { a: PreviewCardMarkdownAnchor }
        : {}),
      ...(zoomableImages ? { img: ZoomableMarkdownImage } : {}),
      // remark-gfm はタスクリストのチェックボックスを `disabled` 付きで描画する。
      // 対話モードでは `disabled` を除外しないと onChange が発火しないため、
      // ここで明示的に取り除く。
      input: ({ type, checked, disabled, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => {
        if (type === 'checkbox') {
          const index = checkboxCounter.current;
          checkboxCounter.current += 1;
          if (interactiveCheckboxes && onCheckboxToggle) {
            return (
              <input
                type="checkbox"
                className="task-list-item-checkbox"
                checked={!!checked}
                onChange={() => onCheckboxToggle(index, !checked)}
                {...props}
              />
            );
          }
          return (
            <input
              type="checkbox"
              className="task-list-item-checkbox"
              checked={checked}
              disabled={disabled}
              readOnly
              {...props}
            />
          );
        }
        return <input type={type} disabled={disabled} {...props} />;
      },
      // rehypeMentionSpans が生成した `<span data-mention>` のメンション直後に、
      // 呼び出し側の suffix（オファーステータスのバッジ等）を差し込む。
      ...(renderMentionSuffix
        ? {
            span: ({
              node,
              children,
              ...props
            }: React.HTMLAttributes<HTMLSpanElement> & { node?: unknown }) => {
              void node;
              const attrs = props as Record<string, unknown>;
              const mention = attrs["data-mention"];
              if (typeof mention === "string" && mention) {
                const rawHours = attrs["data-mention-hours"];
                const hours =
                  typeof rawHours === "string" ? Number(rawHours) : NaN;
                return (
                  <span {...props}>
                    {children}
                    {renderMentionSuffix(
                      mention,
                      Number.isFinite(hours) ? hours : null,
                    )}
                  </span>
                );
              }
              return <span {...props}>{children}</span>;
            },
          }
        : {}),
    };
    // checkboxCounter は ref なので依存に含めない
  }, [
    attachmentDisplay,
    zoomableImages,
    interactiveCheckboxes,
    onCheckboxToggle,
    renderMentionSuffix,
  ]);

  // rehype プラグイン列。メンション装飾・チェックボックス配色は
  // それぞれの prop が渡されたときだけ差し込む（いずれも sanitize の後段）。
  const rehypePlugins = useMemo(() => {
    type RehypePlugins = NonNullable<
      React.ComponentProps<typeof ReactMarkdown>["rehypePlugins"]
    >;
    const plugins: RehypePlugins = [
      rehypeRaw,
      [rehypeSanitize, sanitizeOptions],
      rehypeBreakNewlines,
    ];
    if (renderMentionSuffix) plugins.push(rehypeMentionSpans);
    if (checkboxColorOf) {
      plugins.push(() => (tree: unknown) =>
        walkTaskListCheckboxColors(tree as HastNode, checkboxColorOf),
      );
    }
    return plugins;
  }, [renderMentionSuffix, checkboxColorOf]);

  return (
    <MarkdownContainer
      ref={containerRef}
      className={className}
      onClick={onClick}
      $variant={variant}
    >
      {prefix}
      <ReactMarkdown
        components={components}
        remarkPlugins={breaks ? [remarkGfm, remarkBreaks] : [remarkGfm]}
        rehypePlugins={rehypePlugins}
      >
        {content}
      </ReactMarkdown>
    </MarkdownContainer>
  );
};

export default MarkdownContent;
